// Pick / upload / share / remove the artwork for ONE device type.
//
// Three ways to set an image, which is the whole feature:
//   • Upload a new file               -> uploadDeviceTypeImage -> a fresh URL
//   • Choose one already in the catalog -> the SAME URL as another product
//     (this is how two look-alike products share one image)
//   • Remove                           -> ImageLocation = ''
//
// It only returns the chosen URL to the parent via onSaved; the parent writes
// the row and updates the list. Lives in the desktop pages folder because
// /device-types is a desktop page that both shells route to.

import React, { useEffect, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, CircularProgress, Alert, ImageList, ImageListItem, Tooltip, Stack,
  LinearProgress,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import { uploadDeviceTypeImage, IMAGE_EXTS } from '../../../core/devices/deviceTypeImages';
import { FIT_SM } from '../../shared/fitDialog';

const accept = IMAGE_EXTS.map((e) => `.${e}`).join(',');

// A framed preview that degrades to an icon when there is no image or the URL
// fails to load (e.g. an http image blocked on an https page — see note below).
function Preview({ url, size = 120 }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [url]);
  const box = {
    width: size, height: size, borderRadius: 2, border: '1px solid',
    borderColor: 'divider', display: 'flex', alignItems: 'center',
    justifyContent: 'center', overflow: 'hidden', bgcolor: 'action.hover', flex: 'none',
  };
  if (!url || broken) {
    return (
      <Box sx={box}><ImageIcon sx={{ color: 'text.disabled', fontSize: size / 2.5 }} /></Box>
    );
  }
  return (
    <Box sx={box}>
      <img
        src={url} alt="" onError={() => setBroken(true)}
        style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
      />
    </Box>
  );
}

export default function DeviceTypeImageDialog({
  open, row, images = [], onClose, onSaved,
}) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(null);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  // Reset to the row's current image whenever the dialog (re)opens on a row.
  useEffect(() => {
    if (open) {
      setUrl(String(row?.ImageLocation || '').trim());
      setErr(''); setPct(null); setBusy(false); setSaving(false);
    }
  }, [open, row]);

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // let the same file be chosen again after an error
    if (!file) return;
    setErr(''); setBusy(true); setPct(0);
    try {
      const uploaded = await uploadDeviceTypeImage(file, { onProgress: setPct });
      setUrl(uploaded);
    } catch (ex) {
      setErr(ex.message || 'Upload failed.');
    } finally {
      setBusy(false); setPct(null);
    }
  };

  const save = async () => {
    setSaving(true); setErr('');
    try {
      await onSaved(url.trim());
      onClose();
    } catch (ex) {
      setErr(ex.message || 'Could not save.');
      setSaving(false);
    }
  };

  const current = String(row?.ImageLocation || '').trim();
  const dirty = url.trim() !== current;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} {...FIT_SM}>
      <DialogTitle>Image — {row?.DeviceName || `#${row?.DeviceID}`}</DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Preview url={url} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
              <Button
                variant="outlined" size="small" startIcon={<UploadFileIcon />}
                disabled={busy || saving} onClick={() => fileRef.current?.click()}
              >
                {busy ? 'Uploading…' : 'Upload new'}
              </Button>
              <Button
                variant="outlined" size="small" color="error" startIcon={<DeleteOutlineIcon />}
                disabled={busy || saving || !url} onClick={() => setUrl('')}
              >
                Remove
              </Button>
              <input ref={fileRef} type="file" accept={accept} hidden onChange={onPick} />
            </Stack>
            {busy && <LinearProgressSafe value={pct} />}
            {/* The raw URL — editable, so an image hosted elsewhere can be pasted
                and so the shared value is visible. */}
            <TextField
              fullWidth size="small" label="Image URL" sx={{ mt: 1 }}
              value={url} disabled={busy || saving}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://…/uploads/png/…"
            />
          </Box>
        </Stack>

        {images.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Use an image already in the catalog
            </Typography>
            <ImageList cols={5} gap={8} sx={{ m: 0 }}>
              {images.map((im) => {
                const selected = im.url === url.trim();
                return (
                  <Tooltip key={im.url} title={`${im.names.length} product(s): ${im.names.join(', ')}`}>
                    <ImageListItem
                      onClick={() => !busy && !saving && setUrl(im.url)}
                      sx={{
                        cursor: 'pointer', borderRadius: 1, overflow: 'hidden',
                        border: '2px solid', borderColor: selected ? 'primary.main' : 'transparent',
                      }}
                    >
                      <img src={im.url} alt="" loading="lazy" style={{ height: 64, objectFit: 'contain', background: '#0001' }} />
                    </ImageListItem>
                  </Tooltip>
                );
              })}
            </ImageList>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={busy || saving || !dirty}>
          {saving ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Tiny wrapper so an indeterminate upload (null pct) still shows motion.
function LinearProgressSafe({ value }) {
  return value == null
    ? <LinearProgress />
    : <LinearProgress variant="determinate" value={value} />;
}
